import { OrganizationService, useApiGetter, useSnackbar } from '@localstack/services';
import { ContentModal } from '@localstack/ui';
import { ReactElement, useState, useCallback, useRef } from 'react';

import { useAuthProvider } from './useAuthProvider';
import { useDirectSubscribe } from './useDirectSubscribe';

type SubscriptionCallback = () => void | Promise<void>;

type UseSubscribeToFreemiumPlanReturn = {
  isCreatingSubscription: boolean;
  isCheckingEligibility: boolean;
  canSubscribeToFreemiumPlan: boolean;
  memberRemovalModal: ReactElement | null;
  triggerSubscriptionFlow: (callback?: SubscriptionCallback) => Promise<void>;
};

const FREEMIUM_PLAN_ID = 'plan.freemium-2026-03-23';

export const useSubscribeToFreemiumPlan = (): UseSubscribeToFreemiumPlanReturn => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const onSuccessTaskRef = useRef<(() => void) | null>(null);

  const { isWorkspaceAdmin } = useAuthProvider();

  const { data: orgStats, isLoading: isCheckingEligibility } = useApiGetter(
    OrganizationService,
    'getOrganizationStats',
    [],
    { defaultValue: { num_all_members: 0, num_admin_members: 0, num_invitations: 0 } },
  );

  const { subscribeDirectly, isLoading: isCreatingSubscription } = useDirectSubscribe();

  const { showSnackbar } = useSnackbar();

  const orgHasMultipleMembers = orgStats.num_all_members > 1;
  const canSubscribeToFreemiumPlan = !isCheckingEligibility && (isWorkspaceAdmin || !orgHasMultipleMembers);

  const triggerSubscriptionFlow = useCallback(
    async (callback?: SubscriptionCallback) => {
      if (!isWorkspaceAdmin && orgHasMultipleMembers) {
        showSnackbar({
          severity: 'error',
          message: 'Only a workspace admin is allowed to switch to a Hobby plan',
        });
        return;
      }

      if (!orgHasMultipleMembers) {
        await subscribeDirectly({ planId: FREEMIUM_PLAN_ID });
        await callback?.();
        return;
      }

      if (callback) {
        onSuccessTaskRef.current = callback;
      }
      setIsOpen(true);
    },
    [isWorkspaceAdmin, orgHasMultipleMembers, subscribeDirectly, showSnackbar],
  );

  const handleConfirm = async () => {
    setIsOpen(false);
    await subscribeDirectly({ planId: FREEMIUM_PLAN_ID });

    if (onSuccessTaskRef.current) {
      onSuccessTaskRef.current();
      onSuccessTaskRef.current = null;
    }
  };

  const memberRemovalModal = (
    <ContentModal
      open={isOpen}
      dialogTitle="Member removal notice"
      onClose={() => {
        setIsOpen(false);
        onSuccessTaskRef.current = null;
      }}
      onConfirm={handleConfirm}
      dialogContent="Opting into the Hobby plan will remove all other members from your workspace. The Hobby plan is limited to 1 seat and you will not be able to invite others back."
      confirmButtonText="Confirm"
      cancelButtonText="Cancel"
      confirmButtonProps={{ color: 'error' }}
      fullWidth
    />
  );

  return {
    memberRemovalModal,
    isCreatingSubscription,
    isCheckingEligibility,
    canSubscribeToFreemiumPlan,
    triggerSubscriptionFlow,
  };
};
